import { useCallback, useEffect, useState } from 'react'
import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client'

import { useLoading, useServerError } from 'shared/hooks'

import { GET_LABEL_STORYGUIDE, GET_PROJECT_BY_NAME } from 'modulesCommon/graphql/queries'
import { isLoadingVar, labelCodeIdVar, spaceIdVar, storyGuideVar } from 'modulesCommon/store'
import { useCreateFeed } from 'modules/Feed/graphql/mutations'
import { IProject } from 'shared/interfaces'
import { useZonesList } from 'modulesCommon/serviceHooks'
import { Roles } from 'shared/abilities'
import { useCurrentUser } from 'modules/User/useCurrentUser'

const useAddNewFeed = () => {
  const [searchResult, setSearchResult] = useState({})
  const [isErrorOnAddStory, setError] = useState(false)
  const [isEditorActive, toggleEditor] = useState<boolean>(false)

  const storyGuide = useReactiveVar(storyGuideVar)
  const currentLabelId = useReactiveVar(labelCodeIdVar)
  const spaceId = useReactiveVar(spaceIdVar)
  const isEditorLoading = useReactiveVar(isLoadingVar)
  const { user } = useCurrentUser()
  const isSpaceRole = [Roles.SpaceMember, Roles.SpacePO, Roles.SpaceManager].some(
    (role) => role === user?.role?.role_id
  )

  const { zoneList, zoneError, zoneLoading } = useZonesList()

  const { mutate: createFeed, loading: createFeedLoading } = useCreateFeed()
  const [searchItems, { data: searchData, loading: searchLoading, error: searchError }] =
    useLazyQuery(GET_PROJECT_BY_NAME)
  const { loading: storyGuideLoading, error: storyGuideError } = useQuery(GET_LABEL_STORYGUIDE, {
    onCompleted: ({ getLabelStoryGuide }) => storyGuideVar(getLabelStoryGuide.template)
  })

  const toggleHandler = useCallback(() => toggleEditor(!isEditorActive), [isEditorActive])
  const createFeedHandler = useCallback(
    (args) => {
      return createFeed({
        variables: { input: { ...args } }
      }).then(({ data: { createFeed } }) => {
        if (!createFeed?.id) setError(true)
      })
    },
    [createFeed]
  )

  const searchItemsHandler = useCallback(
    (name) => {
      searchItems({
        variables: {
          name,
          label_id: currentLabelId,
          ...(isSpaceRole && { space_id: user?.space_id })
        }
      })
    },
    [searchItems, currentLabelId, isSpaceRole, user]
  )

  useEffect(() => {
    if (searchData) {
      const { __typename, ...rest } = searchData.getCampaignByName

      setSearchResult({
        campaigns: rest?.campaigns?.map((project: IProject) => ({
          ...project,
          isSpaceRole,
          zone: isSpaceRole ? zoneList?.find((zone) => `${zone.id}` === `${project.label_id}`) : ''
        }))
      })
    }
  }, [searchData, zoneList, isSpaceRole])

  useEffect(() => {
    if (isErrorOnAddStory) {
      setTimeout(() => {
        setError(false)
      }, 2000)
    }
  }, [isErrorOnAddStory])

  useLoading({ isLoading: createFeedLoading || searchLoading || storyGuideLoading || zoneLoading })
  useServerError([searchError, storyGuideError, zoneError])

  return {
    createFeedHandler,
    searchItemsHandler,
    searchResult,
    isErrorOnAddStory,
    storyGuide,
    isEditorLoading,
    isEditorActive,
    toggleHandler,
    spaceId,
    currentLabelId
  }
}

export default useAddNewFeed
